import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './App.css';
import Header from './components/Header';
import Notification from './components/Notification';
import HeroText from './components/HeroText';
import ServiceIcons from './components/ServiceIcons';
import ServicePopup from './components/ServicePopup';
import WithdrawPage from './components/WithdrawPage';
import DepositPage from './components/DepositPage';
import ClausePage from './components/ClausePage';
import FAQsPage from './components/FAQsPage';
import AboutPage from './components/AboutPage';
import CertificatePage from './components/CertificatePage';
import EventPage from './components/EventPage';
import VIPLevels from './components/VIPLevels';
import VIPLevelsPage from './components/VIPLevelsPage';
import WelcomeBanner from './components/WelcomeBanner';
import BottomNav from './components/BottomNav';
import Dashboard from './components/Dashboard';
import Optimization from './components/Optimization';
import MyPage from './components/MyPage';
import RecordsPage from './components/RecordsPage';
import PersonalInformationPage from './components/PersonalInformationPage';
import PaymentMethodsPage from './components/PaymentMethodsPage';
import UpdatePasswordPage from './components/UpdatePasswordPage';
import UpdateTransactionPasswordPage from './components/UpdateTransactionPasswordPage';
import NotificationsPage from './components/NotificationsPage';
import SecurityWarningPopup from './components/SecurityWarningPopup';
import UserAuth from './components/UserAuth';
import AdminLogin from './components/admin/AdminLogin';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/AdminDashboard';
import UserManagement from './components/admin/UserManagement';
import UserEditForm from './components/admin/UserEditForm';
import DepositManagement from './components/admin/DepositManagement';
import WithdrawalManagement from './components/admin/WithdrawalManagement';
import WithdrawalLimitManagement from './components/admin/WithdrawalLimitManagement';
import ManualKnifeManagement from './components/admin/ManualKnifeManagement';
import WhatsAppManagement from './components/admin/WhatsAppManagement';
import UnlockRequests from './components/admin/UnlockRequests';
import HeroTextManagement from './components/admin/HeroTextManagement';
import AdminUserManagement from './components/admin/AdminUserManagement';
import ActivityLog from './components/admin/ActivityLog';
import { useAdminAuth } from './hooks/useAdminAuth';
import { useAuth } from './hooks/useAuth';
import { useUserProfile } from './hooks/useUserProfile';

function App() {
  const [activeTab, setActiveTab] = useState('home');
  const [showServicePopup, setShowServicePopup] = useState(false);
  const [showOptimization, setShowOptimization] = useState(false);
  const [showWithdrawPage, setShowWithdrawPage] = useState(false);
  const [showDepositPage, setShowDepositPage] = useState(false);
  const [showClausePage, setShowClausePage] = useState(false);
  const [showFAQsPage, setShowFAQsPage] = useState(false);
  const [showAboutPage, setShowAboutPage] = useState(false);
  const [showCertificatePage, setShowCertificatePage] = useState(false);
  const [showEventPage, setShowEventPage] = useState(false);
  const [showVIPLevelsPage, setShowVIPLevelsPage] = useState(false);
  const [showMyPage, setShowMyPage] = useState(false);
  const [showPersonalInfoPage, setShowPersonalInfoPage] = useState(false);
  const [showPaymentMethodsPage, setShowPaymentMethodsPage] = useState(false);
  const [showUpdatePasswordPage, setShowUpdatePasswordPage] = useState(false);
  const [showUpdateTransactionPasswordPage, setShowUpdateTransactionPasswordPage] = useState(false);
  const [showNotificationsPage, setShowNotificationsPage] = useState(false);
  const [cameFromMyPage, setCameFromMyPage] = useState(false);
  const [showSecurityPopup, setShowSecurityPopup] = useState(false);
  const [userVipLevel, setUserVipLevel] = useState('VIP1'); // Default VIP level, can be updated from user data
  
  // User authentication
  const { user, loading: authLoading, logout: userLogout } = useAuth();
  
  // Load user profile data
  const { userProfile, loading: profileLoading, error: profileError } = useUserProfile(user?.uid);
  
  // Update VIP level from user profile
  useEffect(() => {
    if (userProfile?.vipLevel) {
      setUserVipLevel(userProfile.vipLevel);
    }
  }, [userProfile]);

  // Initialize automatic daily reset scheduler (runs in background)
  useEffect(() => {
    let cleanup = null;
    
    // Only start if user is authenticated (to ensure Firebase is initialized)
    if (user && !authLoading) {
      const initializeAutoReset = async () => {
        try {
          const { startAutomaticDailyReset } = await import('./services/dailyResetService');
          cleanup = startAutomaticDailyReset();
        } catch (error) {
          console.error('Error initializing automatic daily reset:', error);
        }
      };
      
      initializeAutoReset();
    }
    
    // Cleanup on unmount
    return () => {
      if (cleanup) {
        cleanup();
      }
    };
  }, [user, authLoading]);

  // Monitor account deletion in real-time and logout if account is deleted
  useEffect(() => {
    if (user) {
      // If userProfile is null but user is logged in, account might have been deleted
      // The real-time listener will detect this and set an error
      if (userProfile === null && !profileLoading && profileError) {
        // Account has been deleted (document doesn't exist)
        const handleAccountDeletion = async () => {
          try {
            await userLogout();
            // Don't show alert here, error message will be shown by the component
          } catch (error) {
            console.error('Error logging out deleted user:', error);
          }
        };
        handleAccountDeletion();
      } else if (userProfile) {
        // Check if account is marked as deleted or inactive
        if (userProfile.isDeleted || userProfile.isActive === false) {
          const handleAccountDeletion = async () => {
            try {
              await userLogout();
              alert('Your account has been deleted or deactivated. You have been logged out.');
            } catch (error) {
              console.error('Error logging out deleted user:', error);
            }
          };
          handleAccountDeletion();
        }
      }
    }
  }, [user, userProfile, profileLoading, profileError, userLogout]);
  
  // Admin panel state
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [adminSection, setAdminSection] = useState('dashboard');
  const [editingUserId, setEditingUserId] = useState(null);
  const { isAdmin, loading: adminLoading, logout } = useAdminAuth();

  // Check if URL contains /admin to show admin panel
  useEffect(() => {
    if (window.location.pathname.includes('/admin')) {
      setShowAdminPanel(true);
    }
  }, []);

  // Handle user authentication success
  const handleUserAuthSuccess = (user) => {
    // User is now logged in, the app will automatically show the main content
    console.log('User authenticated:', user.email);
  };

  // Handle user logout
  const handleUserLogout = async () => {
    try {
      await userLogout();
      // User will be redirected to login page automatically
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleResetOptimizationLock = () => {
    // Clear the optimization completion count from localStorage for current VIP level
    localStorage.removeItem(`optimization_completed_${userVipLevel}`);
    // Optionally show a confirmation or reload the page
    alert(`Optimization lock has been reset for ${userVipLevel}. You can now start optimizing again!`);
  };

  // Show security popup on page load/refresh
  useEffect(() => {
    setShowSecurityPopup(true);
  }, []);

  // Test Firebase connection on app load
  useEffect(() => {
    const testFirebase = async () => {
      try {
        const { testFirebaseConnection } = await import('./firebase/helpers');
        await testFirebaseConnection();
      } catch (error) {
        console.error('Firebase test error:', error);
      }
    };
    testFirebase();
  }, []);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab !== 'starting') {
      setShowOptimization(false);
    }
  };

  // Admin panel handlers
  const handleAdminLoginSuccess = () => {
    setShowAdminPanel(true);
    setAdminSection('dashboard');
    window.history.pushState({}, '', '/admin');
  };

  const handleAdminLogout = async () => {
    try {
      await logout();
      setShowAdminPanel(false);
      setAdminSection('dashboard');
      setEditingUserId(null);
      window.history.pushState({}, '', '/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleAdminSectionChange = (section) => {
    setAdminSection(section);
    setEditingUserId(null);
  };

  const handleEditUser = (userId) => {
    setEditingUserId(userId);
  };

  const handleUserUpdate = () => {
    setEditingUserId(null);
    setAdminSection('users');
  };

  // Show admin panel if authenticated as admin
  if (showAdminPanel) {
    if (adminLoading) {
      return (
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }

    if (!isAdmin) {
      return (
        <AdminLogin onLoginSuccess={handleAdminLoginSuccess} />
      );
    }

    return (
      <AdminLayout
        activeSection={editingUserId ? 'users' : adminSection}
        onSectionChange={handleAdminSectionChange}
        onLogout={handleAdminLogout}
      >
        {editingUserId ? (
          <UserEditForm
            userId={editingUserId}
            onBack={() => {
              setEditingUserId(null);
              setAdminSection('users');
            }}
            onUpdate={handleUserUpdate}
          />
        ) : adminSection === 'dashboard' ? (
          <AdminDashboard onNavigate={handleAdminSectionChange} />
        ) : adminSection === 'users' ? (
          <UserManagement onEditUser={handleEditUser} />
        ) : adminSection === 'deposits' ? (
          <DepositManagement />
        ) : adminSection === 'withdrawals' ? (
          <WithdrawalManagement />
        ) : adminSection === 'withdrawal-limits' ? (
          <WithdrawalLimitManagement />
        ) : adminSection === 'manual-knife' ? (
          <ManualKnifeManagement />
        ) : adminSection === 'whatsapp' ? (
          <WhatsAppManagement />
        ) : adminSection === 'unlocks' ? (
          <UnlockRequests />
        ) : adminSection === 'hero-text' ? (
          <HeroTextManagement />
        ) : adminSection === 'admin-users' ? (
          <AdminUserManagement />
        ) : adminSection === 'activity' ? (
          <ActivityLog />
        ) : null}
      </AdminLayout>
    );
  }

  // IMPORTANT: Show login/register page FIRST if user is not authenticated
  // This must come BEFORE any other content checks
  if (authLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  // If user is not logged in, show login/register page
  // This blocks access to the main app until user logs in
  if (!user) {
    return <UserAuth onAuthSuccess={handleUserAuthSuccess} />;
  }

  // If user profile shows account is deleted or doesn't exist, show error and logout
  if (user && ((userProfile && (userProfile.isDeleted || userProfile.isActive === false)) || (!userProfile && !profileLoading && profileError))) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', flexDirection: 'column' }}>
        <div className="alert alert-danger" style={{ maxWidth: '500px', margin: '20px' }}>
          <h4 className="alert-heading">
            <i className="bi bi-exclamation-triangle me-2"></i>
            Account Access Denied
          </h4>
          <p>
            {!userProfile || userProfile.isDeleted
              ? 'Your account has been deleted. You cannot access the website.'
              : 'Your account has been deactivated. Please contact support.'}
          </p>
          <hr />
          <button className="btn btn-primary" onClick={handleUserLogout}>
            Return to Login
          </button>
        </div>
      </div>
    );
  }

  // Check if user is admin - prevent admin users from accessing regular user profile views
  // Admins should use the admin panel instead
  if (user && !adminLoading && isAdmin && !showAdminPanel) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '100vh', flexDirection: 'column' }}>
        <div className="alert alert-info" style={{ maxWidth: '500px', margin: '20px' }}>
          <h4 className="alert-heading">
            <i className="bi bi-shield-check me-2"></i>
            Admin Account Detected
          </h4>
          <p>
            You are logged in as an administrator. Admin accounts should access the system through the admin panel.
          </p>
          <hr />
          <div className="d-flex gap-2">
            <button 
              className="btn btn-primary" 
              onClick={() => {
                setShowAdminPanel(true);
                window.history.pushState({}, '', '/admin');
              }}
            >
              Go to Admin Panel
            </button>
            <button className="btn btn-secondary" onClick={handleUserLogout}>
              Logout
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showWithdrawPage) {
    return (
      <div className="App">
        <WithdrawPage 
          onBack={() => {
            setShowWithdrawPage(false);
            if (cameFromMyPage) {
              setShowMyPage(true);
              setCameFromMyPage(false);
            }
          }}
          onOpenVIPLevels={() => {
            setShowWithdrawPage(false);
            setShowVIPLevelsPage(true);
          }}
        />
      </div>
    );
  }

  if (showDepositPage) {
    return (
      <div className="App">
        <DepositPage 
          onBack={() => {
            setShowDepositPage(false);
            if (cameFromMyPage) {
              setShowMyPage(true);
              setCameFromMyPage(false);
            }
          }} 
        />
      </div>
    );
  }

  if (showClausePage) {
    return (
      <div className="App">
        <ClausePage onBack={() => setShowClausePage(false)} />
      </div>
    );
  }

  if (showFAQsPage) {
    return (
      <div className="App">
        <FAQsPage onBack={() => setShowFAQsPage(false)} />
      </div>
    );
  }

  if (showAboutPage) {
    return (
      <div className="App">
        <AboutPage onBack={() => setShowAboutPage(false)} />
      </div>
    );
  }

  if (showCertificatePage) {
    return (
      <div className="App">
        <CertificatePage onBack={() => setShowCertificatePage(false)} />
      </div>
    );
  }

  if (showEventPage) {
    return (
      <div className="App">
        <EventPage onBack={() => setShowEventPage(false)} />
      </div>
    );
  }

  if (showVIPLevelsPage) {
    return (
      <div className="App">
        <VIPLevelsPage 
          userProfile={userProfile}
          onBack={() => setShowVIPLevelsPage(false)} 
        />
      </div>
    );
  }

  if (showUpdateTransactionPasswordPage) {
    return (
      <div className="App">
        <UpdateTransactionPasswordPage 
          onBack={() => {
            setShowUpdateTransactionPasswordPage(false);
            setShowPersonalInfoPage(true);
          }} 
        />
      </div>
    );
  }

  if (showUpdatePasswordPage) {
    return (
      <div className="App">
        <UpdatePasswordPage 
          onBack={() => {
            setShowUpdatePasswordPage(false);
            setShowPersonalInfoPage(true);
          }} 
        />
      </div>
    );
  }

  if (showPersonalInfoPage) {
    return (
      <div className="App">
        <PersonalInformationPage 
          userProfile={userProfile}
          onBack={() => {
            setShowPersonalInfoPage(false);
            setShowMyPage(true);
          }}
          onUpdatePasswordClick={() => {
            setShowPersonalInfoPage(false);
            setShowUpdatePasswordPage(true);
          }}
          onUpdateTransactionPasswordClick={() => {
            setShowPersonalInfoPage(false);
            setShowUpdateTransactionPasswordPage(true);
          }}
        />
      </div>
    );
  }

  if (showPaymentMethodsPage) {
    return (
      <div className="App">
        <PaymentMethodsPage 
          userProfile={userProfile}
          onBack={() => {
            setShowPaymentMethodsPage(false);
            setShowMyPage(true);
          }}
        />
      </div>
    );
  }

  if (showNotificationsPage) {
    return (
      <div className="App">
        <NotificationsPage 
          onBack={() => {
            setShowNotificationsPage(false);
            setShowMyPage(true);
          }}
        />
      </div>
    );
  }

  if (showMyPage) {
    return (
      <div className="App">
        <MyPage 
          userProfile={userProfile}
          onBack={() => setShowMyPage(false)}
          onDepositClick={() => {
            setShowMyPage(false);
            setShowDepositPage(true);
            setCameFromMyPage(true);
          }}
          onWithdrawClick={() => {
            setShowMyPage(false);
            setShowWithdrawPage(true);
            setCameFromMyPage(true);
          }}
          onPersonalInfoClick={() => {
            setShowMyPage(false);
            setShowPersonalInfoPage(true);
          }}
          onPaymentMethodsClick={() => {
            setShowMyPage(false);
            setShowPaymentMethodsPage(true);
          }}
          onNotificationClick={() => {
            setShowMyPage(false);
            setShowNotificationsPage(true);
          }}
          onLogout={handleUserLogout}
        />
      </div>
    );
  }

  return (
    <div className={`App ${activeTab === 'home' ? 'home-page' : ''}`}>
      <Header 
        isStartingPage={activeTab === 'starting'}
        onProfileClick={() => setShowMyPage(true)}
      />
      {activeTab === 'home' && (
        <>
          <HeroText shouldAnimate={!showSecurityPopup} />
          <Notification />
          <ServiceIcons 
            onServiceClick={() => setShowServicePopup(true)}
            onEventClick={() => setShowEventPage(true)}
            onWithdrawalClick={() => {
              setShowWithdrawPage(true);
              setCameFromMyPage(false);
            }}
            onDepositClick={() => {
              setShowDepositPage(true);
              setCameFromMyPage(false);
            }}
            onTermsClick={() => setShowClausePage(true)}
            onFAQsClick={() => setShowFAQsPage(true)}
            onAboutClick={() => setShowAboutPage(true)}
            onCertificateClick={() => setShowCertificatePage(true)}
          />
          <VIPLevels onViewMore={() => setShowVIPLevelsPage(true)} />
          <WelcomeBanner username={userProfile?.username || 'User'} />
        </>
      )}
      {showServicePopup && (
        <ServicePopup onClose={() => setShowServicePopup(false)} />
      )}
      {showSecurityPopup && (
        <SecurityWarningPopup onClose={() => setShowSecurityPopup(false)} />
      )}
      {activeTab === 'starting' && (
        <>
          {!showOptimization ? (
            <Dashboard 
              userProfile={userProfile}
              username={userProfile?.username || 'User'}
              vipLevel={userProfile?.vipLevel || userVipLevel}
              onStartOptimization={() => {
                setShowOptimization(true);
              }}
              onOpenVIPLevels={() => setShowVIPLevelsPage(true)}
            />
          ) : (
            <Optimization 
              vipLevel={userVipLevel}
              userProfile={userProfile}
              onBack={() => setShowOptimization(false)}
              onNavigateToDeposit={() => {
                setShowOptimization(false);
                setShowDepositPage(true);
              }}
            />
          )}
        </>
      )}
      {activeTab === 'records' && (
        <RecordsPage />
      )}
      <BottomNav activeTab={activeTab} onTabChange={handleTabChange} />
    </div>
  );
}

export default App;
